import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["", "brouillon", "en_attente_paiement", "actif", "en_preparation", "jour_j", "termine", "annule"];

export default function MyAdminEvents() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ organizer_first_name: "", organizer_last_name: "", organizer_email: "", name: "", date: "", time: "", lieu: "", adresse: "", ville: "", code_postal: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/myadmin/events", { params: { q, status } }).then(r => setEvents(r.data.events));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/myadmin/events", form);
      toast.success("Événement créé");
      setShowForm(false);
      setForm({ organizer_first_name: "", organizer_last_name: "", organizer_email: "", name: "", date: "", time: "", lieu: "", adresse: "", ville: "", code_postal: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="myadmin-events">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">MyAdmin</p>
          <h1 className="font-serif-luxe text-4xl mt-3">Événements <span className="italic gold-text">({events.length}).</span></h1>
        </div>
        <button data-testid="btn-new-event" onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest">
          <Plus size={13} /> Créer un mariage
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} data-testid="event-form" className="card-luxe p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["organizer_first_name", "Prénom organisateur"], ["organizer_last_name", "Nom organisateur"],
            ["organizer_email", "Email organisateur"], ["name", "Nom de l'événement"],
            ["date", "Date (YYYY-MM-DD)"], ["time", "Heure (HH:MM)"],
            ["lieu", "Lieu"], ["adresse", "Adresse"], ["ville", "Ville"], ["code_postal", "Code postal"],
          ].map(([k, l]) => (
            <label key={k} className="block">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">{l}</span>
              <input required={["organizer_first_name","organizer_last_name","organizer_email","name"].includes(k)} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 w-full bg-black/40 border border-[#DCB457]/25 text-white p-2 text-sm" data-testid={`event-form-${k}`} />
            </label>
          ))}
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" disabled={busy} data-testid="event-form-submit" className="px-4 py-2 bg-[#DCB457] text-black text-xs uppercase tracking-widest disabled:opacity-50">{busy ? "…" : "Créer"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-white/20 text-white/60 text-xs uppercase tracking-widest">Annuler</button>
          </div>
        </form>
      )}

      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, ville, lieu)" className="w-full bg-black/40 border border-[#DCB457]/25 text-white pl-10 pr-3 py-2 text-sm" data-testid="events-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-black/40 border border-[#DCB457]/25 text-white p-2 text-sm" data-testid="events-status-filter">
          {STATUSES.map(s => <option key={s} value={s}>{s || "Tous statuts"}</option>)}
        </select>
      </div>

      <div className="card-luxe overflow-x-auto">
        <table className="w-full text-sm" data-testid="events-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/45 border-b border-[#DCB457]/15">
              <th className="p-3">Nom</th><th className="p-3">Date</th><th className="p-3">Lieu</th><th className="p-3">Statut</th><th className="p-3">Organisateur</th><th className="p-3">Forfait</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-white/40 italic">Aucun événement.</td></tr>
            ) : events.map(e => (
              <tr key={e.id} className="border-b border-[#DCB457]/5 hover:bg-[#DCB457]/5">
                <td className="p-3"><Link to={`/admin/events/${e.id}`} className="hover:text-[#DCB457]">{e.name}</Link></td>
                <td className="p-3 text-white/60">{e.date || "—"}</td>
                <td className="p-3 text-white/60">{e.lieu || "—"}{e.ville ? ` · ${e.ville}` : ""}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest border border-[#DCB457]/30 px-2 py-1 text-[#DCB457]">{e.status}</span></td>
                <td className="p-3 text-white/60 text-xs">{e.organizer_email || "—"}</td>
                <td className="p-3 text-white/60 text-xs">{e.plan?.name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
